
if (!customElements.get('ss-products-stream')) {
  customElements.define('ss-products-stream', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.grid = this.querySelector('.resource-list--grid');
      this.navigation = this.querySelector('.ss-products-pagination');
      this.button = this.querySelector('[data-load-more]');
      this.status = this.querySelector('[data-load-status]');
      this.sentinel = this.querySelector('[data-load-sentinel]');
      if (!this.grid || !this.button || !this.status || !this.sentinel) return;
      this.initialized = true;
      this.nextUrl = this.navigation?.querySelector('a[rel="next"]')?.href || null;
      this.loadedUrls = new Set();
      this.onClick = () => this.loadNext();
      this.button.addEventListener('click', this.onClick);
      if (this.navigation) this.navigation.hidden = true;
      this.button.hidden = !this.nextUrl;
      if ('IntersectionObserver' in window && this.nextUrl) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadNext();
        }, { rootMargin: '600px 0px' });
        this.observer.observe(this.sentinel);
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.button?.removeEventListener('click', this.onClick);
      this.initialized = false;
    }

    async loadNext() {
      if (this.loading || !this.nextUrl || !this.isConnected) return;
      this.loading = true;
      this.button.disabled = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Laster flere produkter …';
      this.controller = new AbortController();
      try {
        const url = new URL(this.nextUrl, window.location.href);
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.hash = '';
        if (this.loadedUrls.has(url.href)) throw new Error('Repeated product page');
        const response = await fetch(url.href, {
          signal: this.controller.signal,
          credentials: 'same-origin'
        });
        if (!response.ok) throw new Error('Product request failed');
        const html = await response.text();
        if (!this.isConnected) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const source = [...documentFragment.querySelectorAll('ss-products-stream')]
          .find(element => element.dataset.sectionId === this.dataset.sectionId);
        const newGrid = source?.querySelector('.resource-list--grid');
        const cards = newGrid ? [...newGrid.children] : [];
        if (!cards.length) throw new Error('No product cards returned');
        const nextUrl = source.querySelector('.ss-products-pagination a[rel="next"]')?.href || null;
        const existingIds = new Set([...this.grid.children].map(card => card.dataset.productId));
        const newCards = cards.filter(card => !existingIds.has(card.dataset.productId));
        if (!newCards.length) throw new Error('No new product cards returned');
        this.grid.append(...newCards.map(card => document.importNode(card, true)));
        this.loadedUrls.add(url.href);
        this.nextUrl = nextUrl;
        this.button.hidden = !this.nextUrl;
        this.button.textContent = 'Vis flere produkter';
        this.status.textContent = this.nextUrl
          ? newCards.length + ' flere produkter er lastet inn.'
          : 'Alle produktene er lastet inn.';
        if (this.nextUrl) this.observer?.observe(this.sentinel);
        else this.observer?.disconnect();
      } catch (error) {
        if (error.name !== 'AbortError' && this.isConnected) {
          this.status.textContent = 'Kunne ikke laste flere produkter. Prøv igjen.';
          this.button.hidden = false;
          this.button.textContent = 'Prøv igjen';
          this.observer?.disconnect();
          this.failed = true;
        }
      } finally {
        this.loading = false;
        this.button.disabled = false;
        this.grid.removeAttribute('aria-busy');
      }
      if (this.nextUrl && !this.failed && this.isConnected && this.observer) {
        requestAnimationFrame(() => {
          const rect = this.sentinel.getBoundingClientRect();
          if (rect.top < window.innerHeight + 600 && rect.bottom >= 0) this.loadNext();
        });
      }
      this.failed = false;
    }
  });
}
